import { getPageTitle } from "@shared/navigation/SideNav/helpers/getPageTitle";
import { trimTrailingSlash } from "@shared/helpers/pathname";
import { detailIdentityKey } from "./detailRoutes";

/**
 * Views (project 13D, "Cada recurso abierto vive en su propia pestaña") — the model.
 *
 * A view is an address the user asked to keep open, plus the partner/customer lens it
 * was opened under. Rules, as agreed in the intent's review thread (2026-09-29):
 *
 * 1. **On demand, never accumulative.** Plain navigation never creates a view; with no
 *    views the dashboard is exactly what it was. A view only appears on an explicit
 *    gesture ("Add view", Cmd/Ctrl+click, middle click).
 * 2. **"Add view" goes there; Cmd/Ctrl+click and middle click stay.** The menu entry takes
 *    the user to the new view (asked for on 2026-09-30, to make it quicker); the modified
 *    clicks open it in the background, as a browser does. Either way the new view glows,
 *    and the first time the screen the user was on becomes view 1, so it is not lost.
 * 3. **Each view keeps its lens.** Activating a view restores the partner/customer it
 *    holds; changing the selector changes only the active view.
 * 4. **Inside a view, navigation is normal**: it moves the active view's address.
 *
 * Everything here is pure so it can be tested without a store or a router.
 */

export const MAX_VIEWS = 10;

/** The `selection` slice, as a view carries it. */
export interface SelectionSnapshot {
  partnerId: number;
  partnerName: string;
  partnerDomain: string;
  tenantId: number;
  tenantName: string;
  tenantDomain: string;
  customerId?: string | null;
  subscriptionId?: string | null;
}

export interface View {
  id: string;
  /** pathname + search, exactly what the router navigates to. */
  url: string;
  selection: SelectionSnapshot;
  /** The resource's own name once resolved; null until then (the page title stands in). */
  label: string | null;
  /** The identity `label` was resolved for — a label is stale once the view moves. */
  labelFor: string | null;
}

/**
 * Areas that are round trips, not places you keep open (intent §2): Admin Center,
 * Settings / Account and support. Entering one never creates a view and leaves no view
 * active; the bar keeps showing what it had.
 */
const NOT_VIEWABLE_PREFIXES = [
  "/account",
  "/kontrol/admin-center",
  "/reflector/admin-center",
  "/reflector-v2/admin-center",
  "/support",
  "/changelog",
  "/request",
  "/login",
  "/redirecting",
  "/callback",
  "/playground",
];

export function isViewablePath(pathname: string): boolean {
  const path = trimTrailingSlash(pathname);
  return !NOT_VIEWABLE_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
}

export function splitUrl(url: string): { pathname: string; search: string } {
  const [pathname, ...rest] = url.split("?");
  return { pathname: pathname || "/", search: rest.length ? `?${rest.join("?")}` : "" };
}

/**
 * What makes two views the same thing. A detail view is its resource — ids are global,
 * so the lens does not enter. Any other screen (a list, an overview) is the screen under
 * a lens: the port list of customer A and the port list of customer B are two views.
 * Query strings on those screens are view state and do not count.
 */
export function viewIdentity(url: string, selection: SelectionSnapshot): string {
  const { pathname, search } = splitUrl(url);
  const detail = detailIdentityKey(pathname, search);
  if (detail) return detail;
  return `${trimTrailingSlash(pathname)}#tenant=${selection.tenantId}`;
}

/** What the view shows before (or without) its resource name. */
export function defaultViewLabel(url: string): string {
  const title = getPageTitle(trimTrailingSlash(splitUrl(url).pathname));
  return title && title.trim() ? title : "View";
}

export function viewTitle(view: View): string {
  return view.label ?? defaultViewLabel(view.url);
}

/**
 * The lens, for the view's second line. Nothing for someone with a single tenant: a
 * customer's views all share it, and repeating it on every view is noise.
 */
export function viewLensLabel(selection: SelectionSnapshot, tenantType: string | undefined): string | null {
  if (tenantType !== "PARTNER" && tenantType !== "GLOBAL_ADMIN") return null;
  const isPartnerView = !selection.tenantId || selection.tenantId === selection.partnerId;
  if (isPartnerView) {
    return selection.partnerName ? `${selection.partnerName} · All customers` : "All customers";
  }
  // A global admin moves across partners, so the customer alone is ambiguous; a partner
  // only ever has its own.
  if (tenantType === "GLOBAL_ADMIN" && selection.partnerName && selection.tenantName) {
    return `${selection.partnerName} · ${selection.tenantName}`;
  }
  return selection.tenantName || null;
}

export function sameSelection(a: SelectionSnapshot, b: SelectionSnapshot): boolean {
  return (
    a.partnerId === b.partnerId &&
    a.tenantId === b.tenantId &&
    a.partnerName === b.partnerName &&
    a.tenantName === b.tenantName &&
    a.partnerDomain === b.partnerDomain &&
    a.tenantDomain === b.tenantDomain &&
    (a.customerId ?? null) === (b.customerId ?? null) &&
    (a.subscriptionId ?? null) === (b.subscriptionId ?? null)
  );
}

export function snapshotSelection(selection: SelectionSnapshot): SelectionSnapshot {
  return {
    partnerId: selection.partnerId,
    partnerName: selection.partnerName,
    partnerDomain: selection.partnerDomain,
    tenantId: selection.tenantId,
    tenantName: selection.tenantName,
    tenantDomain: selection.tenantDomain,
    customerId: selection.customerId ?? null,
    subscriptionId: selection.subscriptionId ?? null,
  };
}

function newView(id: string, url: string, selection: SelectionSnapshot): View {
  return { id, url, selection, label: null, labelFor: null };
}

export type OpenViewPlan =
  /** The target is already open: nothing is added, that view glows. */
  | { kind: "exists"; flashId: string }
  /** No room: nothing is added and nothing is closed for the user (intent §5). */
  | { kind: "full" }
  /** `add` goes to the end of the bar; `activeId` is set only on the first use. */
  | { kind: "added"; add: View[]; activeId: string | null; flashId: string };

export interface OpenViewRequest {
  /** Where the gesture points. */
  url: string;
  /** The lens the gesture happened under — the new view inherits it. */
  selection: SelectionSnapshot;
  /** Where the user stands right now, when that is somewhere a view can hold. */
  current: { url: string } | null;
}

/**
 * Decides what "Add view" does. `makeId` is injected so the plan stays deterministic
 * under test.
 */
export function planOpenView(
  views: readonly View[],
  request: OpenViewRequest,
  makeId: () => string
): OpenViewPlan {
  const targetIdentity = viewIdentity(request.url, request.selection);
  const existing = views.find((v) => viewIdentity(v.url, v.selection) === targetIdentity);
  if (existing) return { kind: "exists", flashId: existing.id };

  // First use: the screen the user is on becomes view 1, active, so the bar shows where
  // they stand. When that screen *is* the target, it is the only view.
  if (views.length === 0 && request.current) {
    const current = newView(makeId(), request.current.url, request.selection);
    if (viewIdentity(current.url, current.selection) === targetIdentity) {
      return { kind: "added", add: [current], activeId: current.id, flashId: current.id };
    }
    if (MAX_VIEWS < 2) return { kind: "full" };
    const target = newView(makeId(), request.url, request.selection);
    return { kind: "added", add: [current, target], activeId: current.id, flashId: target.id };
  }

  if (views.length >= MAX_VIEWS) return { kind: "full" };
  const target = newView(makeId(), request.url, request.selection);
  return { kind: "added", add: [target], activeId: null, flashId: target.id };
}

/**
 * The view to land on when `closingId` closes, or null when nothing needs to move: it
 * was not the active one, or it was the last view (the user stays on the screen, now
 * with no bar). The right-hand neighbour wins, as in a browser.
 */
export function neighbourOnClose(
  views: readonly View[],
  closingId: string,
  activeId: string | null
): View | null {
  if (closingId !== activeId) return null;
  const index = views.findIndex((v) => v.id === closingId);
  if (index === -1) return null;
  return views[index + 1] ?? views[index - 1] ?? null;
}

/**
 * Whether closing `closingId` leaves a single view. One view is no choice to make, so
 * the bar goes away entirely — the user stays on that screen, exactly as with no
 * views (rule 1) — instead of keeping a lone tab that can only be closed.
 */
export function leavesSingleView(views: readonly View[], closingId: string): boolean {
  return views.some((v) => v.id === closingId) && views.length <= 2;
}

/**
 * Which view a location belongs to when no view is active — e.g. Back landed on an
 * address a view holds. Exact address first, then same identity.
 */
export function viewForLocation(
  views: readonly View[],
  url: string,
  selection: SelectionSnapshot
): View | null {
  const exact = views.find((v) => v.url === url);
  if (exact) return exact;
  const identity = viewIdentity(url, selection);
  return views.find((v) => viewIdentity(v.url, v.selection) === identity) ?? null;
}
